import { useEffect, useState } from "react";
import axios from "axios";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { API } from "@/lib/auth";

export const money = (v, d) => (v == null ? "—" : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: d ?? (v < 1 ? 4 : 2), maximumFractionDigits: d ?? (v < 1 ? 6 : 2) })}`);
export const compact = (v) => (v == null ? "—" : `$${Intl.NumberFormat(undefined, { notation: "compact", maximumFractionDigits: 2 }).format(v)}`);
export const pctTxt = (v) => (v == null ? "—" : `${v >= 0 ? "+" : ""}${Number(v).toFixed(2)}%`);
export const ago = (ts) => {
  if (!ts) return "—";
  const s = Math.max(0, Math.round(Date.now() / 1000 - ts));
  return s < 60 ? `${s}s ago` : s < 3600 ? `${Math.round(s / 60)}m ago` : new Date(ts * 1000).toLocaleString();
};

export const Freshness = ({ d, testid = "freshness" }) => (
  <div className="flex items-center gap-1.5 text-[11px] text-slate-500 font-num" data-testid={testid}>
    <span className={`w-1.5 h-1.5 rounded-full ${d?.stale ? "bg-amber-400" : "bg-emerald-400 pulse-dot"}`} />
    {d?.stale ? <span className="text-amber-300">Delayed — {d.stale_reason}</span> : "Live"} · {d?.source} · updated {ago(d?.fetched_at)}
  </div>
);

export const Skel = ({ h = "h-24" }) => <div className={`${h} rounded-2xl shimmer`} />;

export const ErrorBox = ({ msg, onRetry }) => (
  <div className="glass p-5 text-sm text-red-200 border-red-500/30" data-testid="error-box">
    {msg} {onRetry && <button onClick={onRetry} className="ml-2 underline text-[var(--cyan)]" data-testid="error-retry-btn">Retry</button>}
  </div>
);

export const PriceChart = ({ asset, height = 240 }) => {
  const [range, setRange] = useState("1D");
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let on = true;
    setErr(null);
    const load = () => axios.get(`${API}/crypto/candles/${asset}`, { params: { range } })
      .then((r) => on && setD(r.data)).catch(() => on && setErr("Chart data is unavailable right now."));
    load();
    const id = setInterval(load, 60000);
    return () => { on = false; clearInterval(id); };
  }, [asset, range]);
  const pts = d?.points || [];
  const up = pts.length > 1 && pts[pts.length - 1].close >= pts[0].close;
  const color = up ? "#34D399" : "#F87171";
  const fmt = (t) => new Date(t * 1000).toLocaleString([], range === "1D" ? { hour: "numeric", minute: "2-digit" } : { month: "short", day: "numeric" });
  return (
    <div data-testid="price-chart">
      <div className="flex gap-1 mb-3">
        {["1D", "7D", "30D"].map((r) => (
          <button key={r} onClick={() => setRange(r)} data-testid={`range-${r}`}
            className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${range === r ? "bg-[var(--cyan)] text-[#03141A]" : "text-slate-400 hover:text-white"}`}>{r}</button>
        ))}
      </div>
      {err ? <ErrorBox msg={err} /> : !d ? <Skel h="h-60" /> : (
        <div style={{ height }}>
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={100}>
            <AreaChart data={pts} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
              <defs><linearGradient id={`g-${asset}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={color} stopOpacity={0.35} /><stop offset="100%" stopColor={color} stopOpacity={0} /></linearGradient></defs>
              <XAxis dataKey="t" tickFormatter={fmt} tick={{ fill: "#64748B", fontSize: 10 }} axisLine={false} tickLine={false} minTickGap={40} />
              <YAxis domain={["auto", "auto"]} hide />
              <Tooltip contentStyle={{ background: "#0B1220", border: "1px solid #1A2338", borderRadius: 12, fontSize: 12 }}
                labelFormatter={(t) => new Date(t * 1000).toLocaleString()} formatter={(v) => [money(v), "Price"]} />
              <Area type="monotone" dataKey="close" stroke={color} strokeWidth={2} fill={`url(#g-${asset})`} isAnimationActive />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
      {d && <div className="mt-2"><Freshness d={d} testid="chart-freshness" /></div>}
    </div>
  );
};

const LABEL_STYLE = {
  "Positive Momentum": "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
  "Negative Momentum": "text-red-300 bg-red-500/10 border-red-500/30",
  "Mixed Conditions": "text-amber-200 bg-amber-500/10 border-amber-500/30",
  "Insufficient Data": "text-slate-300 bg-slate-500/10 border-slate-500/30",
};

export const InsightCard = ({ asset, signedIn }) => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    const url = signedIn ? `${API}/crypto/insight/${asset}` : `${API}/crypto/insight-public/${asset}`;
    axios.get(url).then((r) => setD(r.data)).catch((e) => setErr(e.response?.status === 402 ? "Insights for this coin are a Pro feature." :
      e.response?.status === 401 ? "Sign in to see insights for this coin." : "Insight unavailable right now."));
  }, [asset, signedIn]);
  if (err) return <div className="glass p-5 text-sm text-slate-300" data-testid="insight-locked">{err}</div>;
  if (!d) return <Skel h="h-44" />;
  return (
    <section className="glass p-5 sm:p-6" data-testid="insight-card">
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-head font-semibold">Market insight</h3>
        <span className={`px-3 py-1 rounded-full border text-xs font-bold ${LABEL_STYLE[d.label]}`} data-testid="insight-label">{d.label}</span>
      </div>
      <p className="text-sm text-slate-300 mt-3 leading-relaxed" data-testid="insight-explanation">{d.explanation || d.reason}</p>
      {d.trend_pct != null && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4 text-xs font-num">
          {[["Trend (EMA12/48)", pctTxt(d.trend_pct)], ["24h return", pctTxt(d.return_24h_pct)], ["Volatility", `${d.volatility_level} · ±${d.volatility_hourly_pct}%/h`], ["RSI (14h)", d.rsi_14h]].map(([k, v]) => (
            <div key={k} className="rounded-xl bg-white/[0.03] border border-[var(--line)] p-2.5"><div className="text-slate-500 text-[10px] uppercase tracking-wider">{k}</div><div className="mt-1 text-slate-100">{v}</div></div>
          ))}
        </div>
      )}
      {d.uncertainty && <p className="text-xs text-slate-400 mt-3" data-testid="insight-uncertainty">{d.uncertainty}</p>}
      <p className="text-[11px] text-slate-500 mt-3" data-testid="insight-meta">
        {d.timeframe} · {d.data_points} data points from {d.data_source} · analysed {ago(d.analyzed_at)} · {d.method}. Educational only — not financial advice.
      </p>
    </section>
  );
};
